/**
 * Chat del Hiring Manager con el agente de intake (spec §4 "Chat del HM"):
 * burbujas, la JD renderizada cuando llega present_jd_for_review con sus
 * botones "Aprobar" / "Pedir cambios", y el cierre cuando el paquete quedó
 * guardado. Textarea + dictado (VoiceInput inline, mismo arreglo que D-Talk):
 * los HM responden largo y muchas veces por voz.
 *
 * Los turnos van por la Function URL de TA (sin el timeout de CloudFront) y,
 * si no está configurada, por tRPC. El estado vive en el server: cada turno
 * devuelve la vista completa y se escribe en el cache de `taIntake.get`.
 * La Sourcing Guide no existe en esta vista por construcción (spec §3).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeLang } from '@/../../shared/lang';
import { Archive, Check, FileDown, MessageSquareMore, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useAutoResizeTextarea } from '@/components/ui/animated-ai-chat';
import { useChatScroll } from '@/components/chat/useChatScroll';
import { BrainMarkdown } from '@/components/brain/BrainMarkdown';
import { VoiceInput } from '@/components/performance/VoiceInput';
import { STT_LANG } from '@/lib/speech-utils';
import { useArchiveTaIntake, useTaIntakePdf, useTaIntakeSession, useTaIntakeTurnTrpc } from '@/hooks/useTalentAcquisition';
import { intakeTurnDirect, TA_ASK_URL } from '@/lib/ta-api';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { estaGuardada, hmPuedeArchivar, hmPuedeEscribir } from '@/../../shared/ta-intake';
import type { TaIntakeHmView, TaIntakeMessageView, TaIntakeTurnInput } from '@/../../shared/types/ta-intake';
import { TaIntakeJdCard } from './TaIntakeJdCard';
import { TaIntakeStatusBadge } from './TaIntakeStatusBadge';

/** Chip con la acción del HM (aprobó / pidió cambios) — no es texto del modelo ni del HM. */
function ActionChip({ action }: { action: NonNullable<TaIntakeMessageView['action']> }) {
  const { t } = useTranslation();
  const approved = action.kind === 'approved';
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
      approved ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200' : 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200')}>
      {approved ? <Check className="h-3.5 w-3.5" /> : <MessageSquareMore className="h-3.5 w-3.5" />}
      {t(`talent_acquisition.intake.chat.${approved ? 'chip_approved' : 'chip_changes'}`, { version: action.jdVersionId })}
    </span>
  );
}

export function TaIntakeChat({ sessionId, onArchived }: { sessionId: string; onArchived?: () => void }) {
  const { t, i18n } = useTranslation();
  const k = 'talent_acquisition.intake.chat';
  // El idioma de la pantalla manda: el dictado (auditoría del front 25/09, un HM en inglés
  // dictaba y la API lo interpretaba como español) y también la respuesta del agente (Sol 27/09).
  const uiLang = normalizeLang(i18n.language);
  const utils = trpc.useUtils();
  const session = useTaIntakeSession(sessionId);
  const turnTrpc = useTaIntakeTurnTrpc();
  const pdf = useTaIntakePdf();
  const archive = useArchiveTaIntake();
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [recording, setRecording] = useState(false);
  /** "Pedir cambios" apretado: el próximo mensaje va como changes_requested sobre esa versión. */
  const [changesFor, setChangesFor] = useState<string | null>(null);
  const focusAfterVoiceRef = useRef(false);
  const { textareaRef, adjustHeight } = useAutoResizeTextarea({ minHeight: 40, maxHeight: 180 });
  useEffect(() => {
    adjustHeight();
    if (!focusAfterVoiceRef.current) return;
    focusAfterVoiceRef.current = false;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [input, adjustHeight, textareaRef]);

  const view = session.data;
  const bottomRef = useChatScroll([view?.messages.length, pending, changesFor]);
  // Cambió de sesión: se descarta lo tipeado y el modo "pedir cambios"
  useEffect(() => { setInput(''); setChangesFor(null); }, [sessionId]);

  const aplicar = (next: TaIntakeHmView) => {
    utils.taIntake.get.setData({ sessionId }, next);
    utils.taIntake.listMine.invalidate();
    if (estaGuardada(next.session.status)) utils.taIntake.list.invalidate();
  };

  const turno = async (turnInput: TaIntakeTurnInput) => {
    if (pending) return;
    setPending(true);
    try {
      const result = TA_ASK_URL
        ? await intakeTurnDirect({ sessionId, input: turnInput, lang: uiLang })
        : await turnTrpc.mutateAsync({ sessionId, input: turnInput, lang: uiLang });
      aplicar(result.view);
      setChangesFor(null);
    } catch (e) {
      toast.error(t(`${k}.error`, { error: e instanceof Error ? e.message : String(e) }));
      // El texto vuelve al input para reintentar; una decisión con botón se puede repetir
      if (turnInput.kind === 'text') setInput(turnInput.text);
      if (turnInput.kind === 'changes') { setInput(turnInput.comments); setChangesFor(turnInput.jdVersionId); }
      utils.taIntake.get.invalidate({ sessionId });
    } finally {
      setPending(false);
    }
  };

  const send = () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    void turno(changesFor ? { kind: 'changes', jdVersionId: changesFor, comments: text } : { kind: 'text', text });
  };

  if (session.isLoading) return <div className="flex items-center justify-center py-16"><WinclapLoader size={32} /></div>;
  if (session.error || !view) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t(`${k}.load_error`)}</AlertTitle>
        <AlertDescription>{session.error?.message}</AlertDescription>
      </Alert>
    );
  }

  const { session: s, messages, jdVersions, pendingJdVersionId, approvedJdVersionId } = view;
  const versionById = new Map(jdVersions.map((v) => [v.id, v]));
  const abierta = hmPuedeEscribir(s.status);
  const guardada = estaGuardada(s.status);

  return (
    <div className="flex h-full min-h-[60vh] flex-col rounded-xl border bg-background">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
        <TaIntakeStatusBadge status={s.status} />
        <span className="truncate text-sm font-medium">{s.jobTitle || t(`${k}.untitled`)}</span>
        <span className="ml-auto flex items-center gap-1.5">
          {approvedJdVersionId && (
            <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={pdf.isPending} onClick={() => pdf.mutate({ sessionId, doc: 'jd' })}>
              {pdf.isPending ? <WinclapLoader size={14} /> : <FileDown className="h-3.5 w-3.5" />}{t(`${k}.download_jd`)}
            </Button>
          )}
          {hmPuedeArchivar(s.status) && (
            <Button
              size="sm" variant="ghost" className="h-8 gap-1.5 text-muted-foreground" disabled={archive.isPending || pending}
              onClick={() => { if (window.confirm(t(`${k}.archive_confirm`))) archive.mutate({ sessionId }, { onSuccess: () => onArchived?.() }); }}
            >
              <Archive className="h-3.5 w-3.5" />{t(`${k}.archive`)}
            </Button>
          )}
        </span>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {messages.map((m) => {
          const version = m.jdVersionId ? versionById.get(m.jdVersionId) : undefined;
          return (
            <div key={m.id} className="space-y-2">
              {(m.text || m.action) && (
                <div className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                  <div className={cn('max-w-[88%] rounded-lg px-3 py-2 text-sm', m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground')}>
                    {m.action && <div className={cn(m.text && 'mb-1.5')}><ActionChip action={m.action} /></div>}
                    {m.role === 'user'
                      ? m.text && <p className="whitespace-pre-wrap">{m.text}</p>
                      : <div className="prose prose-sm max-w-none dark:prose-invert"><BrainMarkdown content={m.text || t(`${k}.jd_default_line`)} /></div>}
                  </div>
                </div>
              )}
              {version && (
                <TaIntakeJdCard
                  version={version}
                  canDecide={version.id === pendingJdVersionId && abierta && !changesFor}
                  pending={pending}
                  defaultOpen={version.id === pendingJdVersionId || version.id === approvedJdVersionId}
                  onApprove={(id) => void turno({ kind: 'approve', jdVersionId: id })}
                  onRequestChanges={(id) => { setChangesFor(id); requestAnimationFrame(() => textareaRef.current?.focus()); }}
                />
              )}
            </div>
          );
        })}
        {pending && (
          <div className="flex justify-start">
            <div className="animate-pulse rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
              {s.status === 'JD_APPROVED' ? t(`${k}.thinking_saving`) : s.status === 'JD_REVISING' || (s.status === 'JD_REVIEW' && changesFor) ? t(`${k}.thinking_revising`) : t(`${k}.thinking`)}
            </div>
          </div>
        )}
        {guardada && (
          <Alert className="border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40">
            <Check className="h-4 w-4 text-emerald-600" />
            <AlertTitle>{t(`${k}.saved_title`)}</AlertTitle>
            <AlertDescription>{t(`${k}.saved_desc`)}</AlertDescription>
          </Alert>
        )}
        <div ref={bottomRef} />
      </div>

      {abierta && (
        <div className="border-t p-3">
          {changesFor && (
            <div className="mb-2 flex items-center gap-2 rounded-md bg-amber-50 px-3 py-1.5 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              <MessageSquareMore className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1">{t(`${k}.changes_mode`, { version: changesFor })}</span>
              <button type="button" onClick={() => setChangesFor(null)} className="rounded p-0.5 hover:bg-amber-100 dark:hover:bg-amber-900" aria-label={t('common.cancel', 'Cancelar')}><X className="h-3.5 w-3.5" /></button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <VoiceInput
              variant="inline"
              lang={STT_LANG[uiLang]}
              onRecordingChange={setRecording}
              onTranscript={(txt) => { focusAfterVoiceRef.current = true; setInput((prev) => (prev ? `${prev} ${txt}` : txt)); }}
            />
            {/* hidden (no unmount) para no perder lo tipeado mientras graba */}
            <Textarea
              ref={textareaRef} value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={changesFor ? t(`${k}.placeholder_changes`) : t(`${k}.placeholder`)} rows={1} maxLength={6000}
              className={cn('flex-1 resize-none text-sm min-h-0 max-h-44 overflow-y-auto', recording && 'hidden')} disabled={pending}
            />
            <Button onClick={send} disabled={!input.trim() || pending} size="icon" className={cn('h-10 w-10 shrink-0 rounded-full', recording && 'hidden')} aria-label={t(`${k}.send`)}>
              {pending ? <WinclapLoader size={14} /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">{t(`${k}.hint`)}</p>
        </div>
      )}
    </div>
  );
}
